import React, { useMemo, useState } from "react";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { MapPin, ChevronRight, Info } from "lucide-react";

// v14ao — AutoMeasure™ · Details dialog.
// v14at-p3c — Legacy view-based UI opgeruimd:
//   - "Voorgevel"-rij verwijderd (front_cardinal is nu enkel provenance-metadata).
//   - "Bruto exterieur" (GRB-totaal) vervangen door "Schilderbare gevels"
//     berekend uit dezelfde default paintable-scope als PaintMeasure.
//   - Sectie "Hoogtes" hernoemd naar "Gebouwsamenvatting" met duidelijke
//     uitleg dat H1/H2 gebouwbrede/fallback-waarden zijn en dat de engine
//     per-gevel sampling gebruikt voor de gegenereerde gevelvlakken.
// Behouden: Bronobjecten, Bouwdelen, Technische segmenten (raw).

function Row({ label, children }) {
  return (
    <div className="grid grid-cols-[130px_1fr] gap-2 text-[13px] py-1 border-b border-[#E5E2DC] last:border-0">
      <div className="text-[#5D7259] text-[11px] uppercase tracking-wider font-semibold pt-1">{label}</div>
      <div className="text-[#2C2C2A]">{children}</div>
    </div>
  );
}

function ConfBadge({ conf }) {
  const map = {
    high:   { cls: "bg-[#E9F0E3] text-[#3F5E3F] border-[#6B8E6B]/40", txt: "High" },
    medium: { cls: "bg-[#FFF5EF] text-[#8B4A2E] border-[#D4A373]",     txt: "Medium" },
    low:    { cls: "bg-[#FDF5EB] text-[#8B7355] border-[#D4A373]",     txt: "Low" },
    unavailable: { cls: "bg-[#F0EFEA] text-[#A8A7A0] border-[#E5E2DC]", txt: "Onbeschikbaar" },
  };
  const c = map[conf] || map.unavailable;
  return <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-semibold border ${c.cls}`}>{c.txt}</span>;
}

export function AutoMeasureDetailsContent({ building }) {
  const [showSegments, setShowSegments] = useState(false);
  // v14at-p3c — Bereken de default paintable-scope EXACT zoals PaintMeasure
  // `createSurfacesFromComponents` doet: components met include_by_default=true,
  // physical_facades die niet-shared én paintable_default zijn.
  const paintableScope = useMemo(() => {
    if (!building) return { n: 0, area: 0 };
    let n = 0, area = 0;
    for (const c of building.components || []) {
      if (!c.include_by_default) continue;
      for (const f of c.physical_facades || []) {
        if (f.is_shared || !f.is_paintable_default) continue;
        n += 1;
        area += f.effective_area_m2 || 0;
      }
    }
    return { n, area: Math.round(area * 100) / 100 };
  }, [building]);
  if (!building) return null;
  const heightConf = (h) => h?.confidence || "unavailable";
  return (
    <div className="space-y-3" data-testid="automeasure-details-content">
      <div className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3 space-y-0.5">
        <Row label="Gemeten gebouw">
          <div className="font-medium">{building.measurement_address || "—"}</div>
          <div className="text-[11px] text-[#A8A7A0] font-mono">ID {building.building_id || "—"}</div>
        </Row>
        <Row label="Provider">{building.provider || "—"}</Row>
        <Row label="Dakvorm">{building.roof_type_guess || "—"}</Row>
        <Row label="Footprint">{building.footprint_area_m2 ?? "—"} m²</Row>
        <Row label="Schilderbare gevels">
          <span data-testid="automeasure-details-paintable-scope">
            <strong className="font-mono">{paintableScope.n}</strong>{" "}
            gevelvlak{paintableScope.n === 1 ? "" : "ken"} ·{" "}
            <strong className="font-mono">
              {paintableScope.area.toString().replace(".", ",")}
            </strong>{" "}
            m²
          </span>
          <div className="text-[11px] text-[#5D7259] mt-0.5">
            Default paintable-scope — identiek aan de gevelvlakken die
            PaintMeasure automatisch aanmaakt.
          </div>
        </Row>
      </div>

      <div className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3 space-y-0.5" data-testid="automeasure-details-heights">
        <div className="text-[12px] font-semibold text-[#2C2C2A] pb-1">Gebouwsamenvatting</div>
        <div className="text-[11px] text-[#5D7259] pb-1.5">
          H1/H2 zijn algemene, automatisch afgeleide waarden voor het
          hele gebouw. Ze dienen als fallback wanneer per-gevel sampling
          ontbreekt. De AutoMeasure-engine gebruikt waar beschikbaar
          per-gevelsampling voor elk gegenereerd gevelvlak — de
          individuele waarden zie je in de gevel-rijen van PaintMeasure.
        </div>
        <Row label="H1 (dakgoot)">
          <span className="font-mono">{building.H1?.value_m ?? "—"} m</span>
          <span className="ml-2 text-[11px]">— <em>gebouwbrede fallback</em> · <ConfBadge conf={heightConf(building.H1)} /></span>
        </Row>
        <Row label="H2 (nokhoogte)">
          <span className="font-mono">{building.H2?.value_m ?? "—"} m</span>
          <span className="ml-2 text-[11px]">— <em>gebouwbrede fallback</em> · <ConfBadge conf={heightConf(building.H2)} /></span>
        </Row>
      </div>

      <div className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3">
        <div className="text-[12px] font-semibold text-[#2C2C2A] pb-1">Bronnen</div>
        <ul className="space-y-1 text-[12px] text-[#4F4F4D]" data-testid="automeasure-details-sources">
          {(building.sources || []).map((s, i) => (
            <li key={i} className="flex gap-2">
              <ChevronRight className="w-3 h-3 mt-0.5 text-[#A8A7A0] shrink-0" />
              <div>
                <strong>{s.dataset}</strong> — {s.provider}
                <span className="text-[#A8A7A0]"> · v={s.version || "—"} · vintage={s.vintage || "—"}</span>
              </div>
            </li>
          ))}
        </ul>
        <div className="mt-2 text-[11px] italic text-[#A8A7A0]">Licentie: {building.sources?.[0]?.license || "—"}</div>
      </div>

      <div className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3">
        <Row label="Opgehaald">{building.retrieved_at ? new Date(building.retrieved_at).toLocaleString("nl-BE") : "—"}</Row>
      </div>

      {/* v14ar — Bronobjecten (Level A) */}
      {(building.source_objects || []).length > 0 && (
        <div
          className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3"
          data-testid="automeasure-details-source-objects"
        >
          <div className="text-[12px] font-semibold text-[#2C2C2A] pb-1">
            Bronobjecten ({building.source_objects.length})
          </div>
          <div className="text-[11px] text-[#5D7259] pb-2">
            Officiële GRB-objecten rond het adres. Classificatie is een
            schatting op basis van open data — u bepaalt zelf welke bouwdelen
            in het project meegenomen worden.
          </div>
          <div className="space-y-1.5">
            {building.source_objects.map((o, i) => (
              <div
                key={i}
                className="rounded border border-[#E5E2DC] bg-white px-2.5 py-1.5"
                data-testid={`automeasure-details-source-object-${i}`}
              >
                <div className="flex items-center gap-2">
                  <div className="text-[12.5px] font-medium text-[#2C2C2A] flex-1">
                    {o.component_label || "Onzeker bouwdeel"}
                    {o.is_anchor && (
                      <span className="ml-2 text-[10px] px-1.5 py-0.5 rounded bg-[#F2F5EE] text-[#3F5E3F] border border-[#6B8E6B]/40">
                        adres-anchor
                      </span>
                    )}
                  </div>
                  <ConfBadge conf={o.classification_confidence} />
                </div>
                <div className="text-[10.5px] text-[#5D7259] mt-0.5 flex flex-wrap gap-x-3">
                  {o.area_m2 != null && <span>{o.area_m2.toFixed(1)} m²</span>}
                  {!o.is_anchor && o.distance_to_anchor_m != null && (
                    <span>{o.distance_to_anchor_m.toFixed(1)} m van anchor</span>
                  )}
                  {o.touches_anchor && !o.is_anchor && <span>raakt anchor aan</span>}
                </div>
                <div className="text-[10px] text-[#A8A7A0] font-mono mt-0.5">
                  {o.object_class}
                  {o.type ? ` TYPE=${o.type}` : ""}
                  {o.type_label ? ` (${o.type_label})` : ""}
                  {o.feature_id ? ` · ${o.feature_id.split(".").slice(-1)[0]}` : ""}
                  {o.grb_begindatum ? ` · sinds ${o.grb_begindatum.slice(0, 10)}` : ""}
                </div>
              </div>
            ))}
          </div>
          {(building.components || []).length > 0 && (
            <div className="mt-3 pt-2 border-t border-[#E5E2DC]">
              <div className="text-[11px] font-semibold text-[#2C2C2A] pb-1">
                Bouwdelen ({building.components.length})
              </div>
              <div className="space-y-1">
                {building.components.map((c, i) => (
                  <div key={i} className="flex items-center gap-2 text-[11px]">
                    <span className="w-2 h-2 rounded-full bg-[#6B8E6B]" />
                    <span className="font-medium text-[#2C2C2A] flex-1">{c.label}</span>
                    <span className="text-[#5D7259]">
                      {c.n_physical_facades} gevelvlak{c.n_physical_facades === 1 ? "" : "ken"}
                    </span>
                    <ConfBadge conf={c.classification_confidence} />
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <details
        open={showSegments}
        onToggle={(e) => setShowSegments(e.target.open)}
        className="rounded-lg bg-[#FAF9F6] border border-[#E5E2DC] p-3"
        data-testid="automeasure-details-segments"
      >
        <summary className="cursor-pointer text-[12px] font-semibold text-[#2C2C2A]">
          Toon technische segmenten ({(building.segments || []).length})
        </summary>
        <div className="mt-2 space-y-1">
          {(building.segments || []).map((s, i) => (
            <div key={i} className="flex items-center gap-2 text-[11px] font-mono px-2 py-1 rounded bg-white border border-[#E5E2DC]">
              <span className="w-14">{s.length_m?.toFixed(2)} m</span>
              <span className="text-[10px] px-1.5 py-0.5 rounded bg-[#FAF9F6] border border-[#E5E2DC] text-[#4F4F4D] w-10 text-center">{s.cardinal || "?"}</span>
              <span className="text-[10px] text-[#A8A7A0]">bearing {Math.round(s.bearing_deg || 0)}° · gevel {Math.round(s.orientation_deg || 0)}°</span>
              {s.is_primary && <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-[#FFF5EF] text-[#8B4A2E] border border-[#D4A373]">nok-richting</span>}
            </div>
          ))}
        </div>
      </details>

      <div className="rounded bg-[#FFF5EF] border border-[#D4A373] px-3 py-2 text-[11px] text-[#8B4A2E] flex items-start gap-1.5">
        <Info className="w-3.5 h-3.5 mt-0.5 shrink-0" />
        <div>AutoMeasure is een hulpmiddel op basis van officiële Vlaamse open data. Alle waarden moeten door u op de werf gecontroleerd worden. Bruto per gevelvlak wordt door PaintMeasure exact herberekend uit L × H (rechthoek) of L × (H1+H2)/2 (gable-end).</div>
      </div>
    </div>
  );
}

// v14at-p4 F5.1 (Defect J) — De details-dialog is nu een thin wrapper om
// `AutoMeasureDetailsContent`. Review-dialog gebruikt de content inline
// (geen tweede Dialog + Radix Portal → geen focus/pointer-events trap).
export default function AutoMeasureDetailsDialog({ open, onOpenChange, building }) {
  if (!building) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="automeasure-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <MapPin className="w-4 h-4 text-[#D96C4A]" /> AutoMeasure™ · Details
          </DialogTitle>
        </DialogHeader>
        <AutoMeasureDetailsContent building={building} />
      </DialogContent>
    </Dialog>
  );
}
